import React, { useEffect, useMemo, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { API } from "@/lib/api";
import resolveImageUrl from "@/lib/imageUrl";
import ImagePicker from "./ImagePicker";

// ─────────────────────────────────────────────────────────────────────────
// Human-friendly names shown in the section list & editor headers.
// Keep in sync with server-side HOME_DEFAULT_SECTIONS slugs.
const SECTION_META = {
  hero_church:      { name: "Hero",                blurb: "Main image of the Home", images: { min: 1, max: 1, slots: ["Hero photo"] }, focalOnMobile: true },
  origin_story:     { name: "Our Beginning — DANA 2024", blurb: "Origin story", images: { min: 0, max: 3, slots: ["Main photo", "Photo 2", "Photo 3"] } },
  social_run_recap: { name: "Social Run",          blurb: "Editorial recap of the Social Run", images: { min: 0, max: 6, slots: ["Main photo", "Photo 2", "Photo 3", "Photo 4", "Photo 5", "Photo 6"] } },
  next_event_kb:    { name: "Next Event — KB Hallen", blurb: "Manual event teaser", images: { min: 0, max: 1, slots: ["Event photo"] } },
  raffle_module:    { name: "Charity Raffle",      blurb: "Presentation only — live numbers stay dynamic", images: { min: 0, max: 4, slots: ["Section image", "Prize 2", "Prize 3", "Prize 4"] } },
  denmark_spain:    { name: "Denmark + Spain",     blurb: "Cross-country connection", images: { min: 0, max: 2, slots: ["Denmark photo", "Spain photo"] } },
  join_danamark:    { name: "Join DANAmark",       blurb: "Call to become a member", images: { min: 0, max: 1, slots: ["Background photo"] } },
  impact_transparency: { name: "Impact & Transparency", blurb: "Stats + supporting photo", images: { min: 0, max: 1, slots: ["Background photo"] } },
  partners_wall:    { name: "Partners",             blurb: "Managed from the Partners table", images: { min: 0, max: 0, slots: [] } },
  final_cta:        { name: "Final CTA",            blurb: "Closing call to action", images: { min: 0, max: 1, slots: ["Background photo"] }, focalOnMobile: true },
};

const humanName = (d) => SECTION_META[d.slug]?.name || d.component || d.slug;
const humanBlurb = (d) => SECTION_META[d.slug]?.blurb || "";
const slotConfig = (d) => SECTION_META[d.slug]?.images || { min: 0, max: 6, slots: [] };

// ─────────────────────────────────────────────────────────────────────────
// HomeAdmin — top-level list of sections with drag-to-reorder + inline edit.
// Each row shows: order · thumbnail · human name · status badges · Edit btn.
export default function HomeAdmin({ authed }) {
  const [sections, setSections] = useState(null);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const r = await authed.get("/content?kind=home_section&status=");
    const rows = (r.data || []).slice().sort((a, b) => ((a.data?.order || 0) - (b.data?.order || 0)));
    setSections(rows);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const seed = async () => {
    if (!confirm("Seed the 10 default Home sections?")) return;
    await authed.post("/admin/home/seed", {});
    setMsg("Seeded default Home sections.");
    await load();
  };

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex(s => s.id === active.id);
    const newIdx = sections.findIndex(s => s.id === over.id);
    const reordered = arrayMove(sections, oldIdx, newIdx).map((s, i) => ({
      ...s, data: { ...s.data, order: i + 1 },
    }));
    setSections(reordered);
    await authed.post("/admin/home/reorder", {
      order: reordered.map((s, i) => ({ id: s.id, order: i + 1 })),
    });
  };

  const toggleVisibility = async (s) => {
    const status = s.status === "published" ? "draft" : "published";
    await authed.put(`/content/${s.id}`, { kind: "home_section", status, data: s.data });
    await load();
  };

  if (sections === null) return <div className="text-ink/50 text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="home-admin">
      <section className="border border-line rounded-xl p-6 bg-white flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="label-mono text-terracotta text-[10px]">HOME PAGE · CMS</div>
          <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">Sections</h2>
          <p className="mt-2 text-xs text-ink/60 max-w-lg">Drag to reorder · click a row to edit content and photos. Each section has a Draft so you can experiment without touching the live site.</p>
        </div>
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="border border-line px-3 py-1.5 text-xs uppercase tracking-widest hover:border-ink">View live</a>
          {sections.length === 0 && (
            <button onClick={seed} data-testid="home-seed" className="bg-terracotta text-white px-3 py-1.5 text-xs uppercase tracking-widest">Seed defaults</button>
          )}
        </div>
        {msg && <div className="w-full text-xs text-ink/60">{msg}</div>}
      </section>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2" data-testid="home-sections-list">
            {sections.map((s, idx) => (
              <SortableHomeRow key={s.id} s={s} idx={idx}
                               onEdit={() => setEditing(s)}
                               onToggle={() => toggleVisibility(s)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {editing && (
        <HomeSectionEditor s={editing} authed={authed}
                           onClose={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function SortableHomeRow({ s, idx, onEdit, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const d = s.data || {};
  const thumb = d.images?.[0]?.url;
  const hidden = s.status !== "published";
  const hasDraft = !!s.data_draft;
  return (
    <div ref={setNodeRef} style={style} data-testid={`home-row-${d.slug}`}
         className={`bg-white border ${hidden ? "border-dashed border-line/60" : "border-line"} rounded-lg flex items-center gap-3 p-3`}>
      <button {...attributes} {...listeners} aria-label="Drag to reorder"
              className="cursor-grab active:cursor-grabbing text-ink/30 hover:text-ink text-xl px-2 select-none">≡</button>
      <div className="font-editorial text-lg tabular-nums w-8 text-ink/40">{idx + 1}</div>
      <div className="w-14 h-14 shrink-0 border border-line/50 bg-line/10 overflow-hidden">
        {thumb ? <img src={resolveImageUrl(thumb)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-ink/25 text-[10px]">—</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-editorial text-base leading-tight truncate">{humanName(d)}</div>
        <div className="text-[11px] text-ink/50 truncate">{humanBlurb(d)}</div>
      </div>
      <div className="flex items-center gap-2">
        {hasDraft && <span className="text-[9px] label-mono tracking-widest px-2 py-1 rounded bg-amber-100 text-amber-800" data-testid={`badge-draft-${d.slug}`}>DRAFT</span>}
        <button onClick={onToggle} data-testid={`toggle-${d.slug}`}
                className={`text-[10px] label-mono tracking-widest px-2.5 py-1 rounded ${hidden ? "bg-line/30 text-ink/60" : "bg-emerald-100 text-emerald-800"}`}>
          {hidden ? "HIDDEN" : "PUBLISHED"}
        </button>
        <button onClick={onEdit} data-testid={`edit-${d.slug}`}
                className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white transition-colors">
          Edit
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Section editor — draft-first workflow.
// Loads existing draft (`s.data_draft`) if present, otherwise starts from
// the published `s.data`. Save Draft = persist without publishing. Publish
// = copy draft → data and clear the draft field.
function HomeSectionEditor({ s, authed, onClose }) {
  const startFromDraft = !!s.data_draft;
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(s.data_draft || s.data || {})));
  const [savingLabel, setSavingLabel] = useState("");
  const [dirty, setDirty] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // slot idx or null
  const [pane, setPane] = useState("content"); // content | media | preview
  const [textLang, setTextLang] = useState("en");
  const cfg = slotConfig(draft);

  const patch = (k, v) => { setDraft(x => ({ ...x, [k]: v })); setDirty(true); };
  const patchLang = (k, lang, v) => { setDraft(x => ({ ...x, [k]: { ...(x[k] || {}), [lang]: v } })); setDirty(true); };

  const saveDraft = async () => {
    setSavingLabel("Saving draft…");
    await authed.put(`/content/${s.id}/draft`, { data: { ...draft, order: s.data?.order || draft.order } });
    setSavingLabel("Draft saved.");
    setDirty(false);
    setTimeout(() => setSavingLabel(""), 1600);
  };

  const publish = async () => {
    if (!confirm("Publish these changes to the live Home page?")) return;
    setSavingLabel("Publishing…");
    await authed.post(`/content/${s.id}/publish`, { data: { ...draft, order: s.data?.order || draft.order } });
    setSavingLabel("Published.");
    setDirty(false);
    setTimeout(() => onClose(), 700);
  };

  const discardDraft = async () => {
    if (!confirm("Discard all draft changes? The live version will remain unchanged.")) return;
    await authed.post(`/content/${s.id}/discard-draft`);
    onClose();
  };

  const setImageAtSlot = (idx, asset) => {
    const images = [...(draft.images || [])];
    const existing = images[idx] || {};
    images[idx] = {
      ...existing,
      url: asset.url,
      alt: existing.alt || { en: asset.alt_text || "", da: asset.alt_text || "", es: asset.alt_text || "" },
    };
    patch("images", images);
    setPickerFor(null);
  };

  const clearSlot = (idx) => {
    const images = [...(draft.images || [])];
    images[idx] = { url: "", alt: images[idx]?.alt || { en: "", da: "", es: "" } };
    patch("images", images);
  };

  const moveSlot = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const images = [...(draft.images || [])];
    while (images.length <= Math.max(fromIdx, toIdx)) images.push({ url: "", alt: { en: "", da: "", es: "" } });
    [images[fromIdx], images[toIdx]] = [images[toIdx], images[fromIdx]];
    patch("images", images);
  };

  const setFocal = (idx, kind, xy) => {
    const images = [...(draft.images || [])];
    const cur = images[idx] || {};
    images[idx] = { ...cur, [kind]: xy };
    patch("images", images);
  };

  const setAlt = (idx, lang, v) => {
    const images = [...(draft.images || [])];
    images[idx] = { ...images[idx], alt: { ...(images[idx].alt || {}), [lang]: v } };
    patch("images", images);
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 flex items-start justify-center overflow-y-auto p-4"
         data-testid={`editor-${draft.slug}`}>
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl my-6" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="p-5 border-b border-line flex items-baseline justify-between gap-4 sticky top-0 bg-white z-10">
          <div>
            <div className="label-mono text-terracotta text-[10px]">
              {startFromDraft ? "EDITING DRAFT" : "EDIT SECTION"}
            </div>
            <h3 className="font-editorial text-2xl tracking-tighter mt-1">{humanName(draft)}</h3>
            <div className="text-[11px] text-ink/50 mt-0.5">{humanBlurb(draft)}</div>
          </div>
          <button onClick={onClose} className="text-ink/50 hover:text-ink text-2xl leading-none px-2" data-testid="editor-close">×</button>
        </div>

        {/* Pane tabs */}
        <div className="px-5 pt-3 flex gap-2 border-b border-line">
          {[["content", "Content"], ["media", "Media"], ["preview", "Preview"]].map(([k, l]) => (
            <button key={k} onClick={() => setPane(k)} data-testid={`editor-pane-${k}`}
                    className={`text-xs uppercase tracking-widest px-3 py-2 ${pane === k ? "border-b-2 border-terracotta text-terracotta -mb-px" : "text-ink/60 hover:text-ink"}`}>{l}</button>
          ))}
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">

          {/* ── CONTENT PANE ────────────────────────────────────── */}
          {pane === "content" && (
            <div className="space-y-6">
              {/* Language switcher */}
              <div className="flex items-center gap-2">
                <div className="label-mono text-ink/60 text-[10px]">LANGUAGE</div>
                {["en", "da", "es"].map(l => (
                  <button key={l} onClick={() => setTextLang(l)} data-testid={`lang-${l}`}
                          className={`text-[10px] label-mono px-3 py-1 rounded ${textLang === l ? "bg-ink text-white" : "bg-line/30 text-ink/60"}`}>
                    {l.toUpperCase()}
                  </button>
                ))}
                <div className="text-[11px] text-ink/40 ml-2">Photos are shared across all languages.</div>
              </div>

              <TextField label="Eyebrow" value={(draft.eyebrow || {})[textLang] || ""}
                         onChange={v => patchLang("eyebrow", textLang, v)} />
              <TextField label="Headline" value={(draft.headline || {})[textLang] || ""}
                         onChange={v => patchLang("headline", textLang, v)} multiline />
              <TextField label="Body text" value={(draft.body || {})[textLang] || ""}
                         onChange={v => patchLang("body", textLang, v)} multiline rows={6} />
              {("pull_quote" in draft) && (
                <TextField label="Pull quote" value={(draft.pull_quote || {})[textLang] || ""}
                           onChange={v => patchLang("pull_quote", textLang, v)} />
              )}

              <div className="grid md:grid-cols-2 gap-6 pt-2 border-t border-line/50">
                <div>
                  <div className="label-mono text-ink/60 text-[10px] mb-2">Primary CTA</div>
                  <TextField compact label="Label" value={(draft.cta_label || {})[textLang] || ""}
                             onChange={v => patchLang("cta_label", textLang, v)} />
                  {["join_danamark", "final_cta"].includes(draft.slug) && (
                    // Membership CTAs — default to reading the global URL
                    // from Site Settings so the admin can flip one switch
                    // to route everywhere. Toggle OFF to hardcode a URL
                    // just for this section without affecting anything else.
                    <label className="mt-2 flex items-center gap-2 text-[11px] text-ink/70 cursor-pointer">
                      <input type="checkbox"
                             checked={draft.use_global_membership_url !== false}
                             onChange={e => patch("use_global_membership_url", e.target.checked)}
                             data-testid={`use-global-membership-${draft.slug}`}
                             className="w-4 h-4 accent-terracotta" />
                      <span>Use global membership URL (Site Settings)</span>
                    </label>
                  )}
                  <input value={draft.cta_url || ""} onChange={e => patch("cta_url", e.target.value)}
                         placeholder="URL (e.g. /raffle#buy)"
                         disabled={["join_danamark", "final_cta"].includes(draft.slug) && draft.use_global_membership_url !== false}
                         className={`mt-2 w-full border border-line px-3 py-2 text-sm ${["join_danamark", "final_cta"].includes(draft.slug) && draft.use_global_membership_url !== false ? "bg-line/10 text-ink/40" : ""}`} />
                </div>
                {("cta2_label" in draft || ["hero_church", "next_event_kb", "join_danamark"].includes(draft.slug)) && (
                  <div>
                    <div className="label-mono text-ink/60 text-[10px] mb-2">Secondary CTA</div>
                    <TextField compact label="Label" value={(draft.cta2_label || {})[textLang] || ""}
                               onChange={v => patchLang("cta2_label", textLang, v)} />
                    <input value={draft.cta2_url || ""} onChange={e => patch("cta2_url", e.target.value)}
                           placeholder="URL"
                           className="mt-2 w-full border border-line px-3 py-2 text-sm" />
                  </div>
                )}
              </div>

              {draft.slug === "next_event_kb" && (
                <div className="grid grid-cols-3 gap-3 pt-2 border-t border-line/50">
                  <div><label className="label-mono text-ink/60 text-[10px]">Date (ISO)</label>
                    <input value={draft.date_iso || ""} onChange={e => patch("date_iso", e.target.value)}
                           placeholder="2026-05-16"
                           className="mt-1 w-full border border-line px-3 py-2 text-sm" /></div>
                  <div><label className="label-mono text-ink/60 text-[10px]">Venue</label>
                    <input value={draft.venue || ""} onChange={e => patch("venue", e.target.value)}
                           className="mt-1 w-full border border-line px-3 py-2 text-sm" /></div>
                  <div><label className="label-mono text-ink/60 text-[10px]">City</label>
                    <input value={draft.city || ""} onChange={e => patch("city", e.target.value)}
                           className="mt-1 w-full border border-line px-3 py-2 text-sm" /></div>
                </div>
              )}

              {("stats" in draft) && <StatsEditor draft={draft} patch={patch} />}

              {draft.slug === "hero_church" && (
                <div className="pt-2 border-t border-line/50">
                  <label className="label-mono text-ink/60 text-[10px]">Overlay intensity ({Math.round((draft.overlay || 0.7) * 100)}%)</label>
                  <input type="range" min="0" max="100" value={Math.round((draft.overlay ?? 0.7) * 100)}
                         onChange={e => patch("overlay", Number(e.target.value) / 100)}
                         className="w-full" data-testid="overlay-slider" />
                  <div className="text-[10px] text-ink/50 mt-1">Higher = darker overlay so light text stays readable.</div>
                </div>
              )}
            </div>
          )}

          {/* ── MEDIA PANE ─────────────────────────────────────── */}
          {pane === "media" && (
            <div>
              {cfg.max === 0 ? (
                <div className="text-sm text-ink/60 border border-dashed border-line rounded p-6">This section has no editable photos here. Manage its images in its own admin table.</div>
              ) : (
                <div className="space-y-4" data-testid="media-slots">
                  {cfg.slots.map((slotName, idx) => (
                    <ImageSlot
                      key={idx} idx={idx}
                      slotName={slotName}
                      image={draft.images?.[idx]}
                      focalOnMobile={!!SECTION_META[draft.slug]?.focalOnMobile}
                      onReplace={() => setPickerFor(idx)}
                      onRemove={() => clearSlot(idx)}
                      onMoveUp={() => idx > 0 && moveSlot(idx, idx - 1)}
                      onMoveDown={() => idx < cfg.slots.length - 1 && moveSlot(idx, idx + 1)}
                      onFocal={(kind, xy) => setFocal(idx, kind, xy)}
                      onAlt={(lang, v) => setAlt(idx, lang, v)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── PREVIEW PANE ───────────────────────────────────── */}
          {pane === "preview" && (
            <SectionPreview draft={draft} lang={textLang} />
          )}
        </div>

        {/* Footer buttons */}
        <div className="p-5 border-t border-line flex justify-between gap-3 sticky bottom-0 bg-white items-center">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="text-ink/60 hover:text-ink px-4 py-2 text-sm uppercase tracking-widest">Cancel</button>
            {startFromDraft && (
              <button onClick={discardDraft} className="text-red-600/80 hover:text-red-600 px-3 py-2 text-xs uppercase tracking-widest" data-testid="discard-draft">Discard draft</button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {savingLabel && <span className="text-[11px] text-ink/50" data-testid="editor-status">{savingLabel}</span>}
            <button onClick={saveDraft} data-testid="editor-save-draft"
                    className={`border border-ink text-ink px-5 py-2 text-sm uppercase tracking-widest hover:bg-ink hover:text-white transition-colors ${dirty ? "" : "opacity-70"}`}>
              Save draft
            </button>
            <button onClick={publish} data-testid="editor-publish"
                    className="bg-terracotta hover:bg-terracotta-dark text-white px-5 py-2 text-sm uppercase tracking-widest">Publish</button>
          </div>
        </div>
      </div>

      {pickerFor !== null && (
        <ImagePicker authed={authed}
                     onPick={(asset) => setImageAtSlot(pickerFor, asset)}
                     onClose={() => setPickerFor(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// A single "slot" with thumb, replace/remove, focal-point control and alt.
function ImageSlot({ idx, slotName, image, focalOnMobile, onReplace, onRemove, onMoveUp, onMoveDown, onFocal, onAlt }) {
  const url = image?.url;
  const focal = image?.focal || { x: 50, y: 50 };
  const focalMobile = image?.focal_mobile || focal;
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div className={`border ${url ? "border-line" : "border-dashed border-line/70"} rounded-lg p-4`} data-testid={`slot-${idx}`}>
      <div className="flex items-start gap-4">
        {/* Thumb + drag preview */}
        <div className="w-40 shrink-0">
          <div className="text-[10px] label-mono text-ink/50 mb-2">{slotName}</div>
          <div className="aspect-[4/3] bg-line/10 border border-line/50 overflow-hidden relative">
            {url ? (
              <img src={resolveImageUrl(url)}
                   alt=""
                   className="w-full h-full object-cover"
                   style={{ objectPosition: `${focal.x}% ${focal.y}%` }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-ink/30 text-xs">No photo</div>
            )}
          </div>
          <div className="mt-2 flex gap-1 flex-wrap">
            <button onClick={onReplace} data-testid={`slot-${idx}-replace`}
                    className="text-[10px] uppercase tracking-widest border border-line px-2 py-1 hover:border-ink">{url ? "Replace" : "Choose"}</button>
            {url && (
              <button onClick={onRemove} data-testid={`slot-${idx}-remove`}
                      className="text-[10px] uppercase tracking-widest border border-line/60 px-2 py-1 text-ink/60 hover:text-red-600 hover:border-red-500">Remove</button>
            )}
            <button onClick={onMoveUp} title="Move up" className="text-[10px] uppercase tracking-widest border border-line/60 px-2 py-1 text-ink/60 hover:border-ink">↑</button>
            <button onClick={onMoveDown} title="Move down" className="text-[10px] uppercase tracking-widest border border-line/60 px-2 py-1 text-ink/60 hover:border-ink">↓</button>
          </div>
        </div>

        {/* Right column: focal + alt */}
        <div className="flex-1 min-w-0">
          {url && (
            <>
              <div className="mb-3 flex flex-wrap gap-4 items-start">
                <div>
                  <div className="text-[10px] label-mono text-ink/50 mb-1">Image focus (desktop)</div>
                  <FocalGrid focal={focal} onChange={xy => onFocal("focal", xy)} />
                </div>
                {focalOnMobile && (
                  <div>
                    <div className="text-[10px] label-mono text-ink/50 mb-1">Image focus (mobile)</div>
                    <FocalGrid focal={focalMobile} onChange={xy => onFocal("focal_mobile", xy)} />
                  </div>
                )}
                {/* Live preview — updates instantly with focal changes.
                    Desktop 16:9 uses `focal`; Mobile 3:4 uses `focal_mobile`
                    when available, otherwise falls back to `focal`. */}
                <div className="flex gap-3 ml-auto">
                  <FocalPreview label="Desktop"
                                url={resolveImageUrl(url)}
                                aspect="16 / 9" width={160}
                                position={`${focal.x}% ${focal.y}%`} />
                  <FocalPreview label="Mobile"
                                url={resolveImageUrl(url)}
                                aspect="3 / 4" width={80}
                                position={`${focalMobile.x}% ${focalMobile.y}%`} />
                </div>
              </div>
              <button onClick={() => setShowAdvanced(x => !x)} className="text-[10px] uppercase tracking-widest text-ink/50 hover:text-ink underline underline-offset-2">
                {showAdvanced ? "Hide advanced" : "Advanced focus"}
              </button>
              {showAdvanced && (
                <div className="mt-2 grid grid-cols-2 gap-3 text-[11px]">
                  <label>Desktop X %<input type="number" min="0" max="100" value={focal.x} onChange={e => onFocal("focal", { ...focal, x: Number(e.target.value) })} className="w-full border border-line px-2 py-1" /></label>
                  <label>Desktop Y %<input type="number" min="0" max="100" value={focal.y} onChange={e => onFocal("focal", { ...focal, y: Number(e.target.value) })} className="w-full border border-line px-2 py-1" /></label>
                  {focalOnMobile && (<>
                    <label>Mobile X %<input type="number" min="0" max="100" value={focalMobile.x} onChange={e => onFocal("focal_mobile", { ...focalMobile, x: Number(e.target.value) })} className="w-full border border-line px-2 py-1" /></label>
                    <label>Mobile Y %<input type="number" min="0" max="100" value={focalMobile.y} onChange={e => onFocal("focal_mobile", { ...focalMobile, y: Number(e.target.value) })} className="w-full border border-line px-2 py-1" /></label>
                  </>)}
                </div>
              )}
              <div className="mt-3 space-y-1.5">
                <div className="text-[10px] label-mono text-ink/50">Alt text (each language)</div>
                {["en", "da", "es"].map(l => (
                  <input key={l} value={image?.alt?.[l] || ""}
                         onChange={e => onAlt(l, e.target.value)}
                         placeholder={`Alt (${l.toUpperCase()})`}
                         className="w-full border border-line px-2 py-1 text-xs" />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// 3x3 focal-point grid.
function FocalGrid({ focal, onChange }) {
  const positions = [[0, 0], [50, 0], [100, 0], [0, 50], [50, 50], [100, 50], [0, 100], [50, 100], [100, 100]];
  const isActive = (x, y) => Math.abs((focal.x || 50) - x) < 8 && Math.abs((focal.y || 50) - y) < 8;
  return (
    <div className="grid grid-cols-3 gap-1 w-24" data-testid="focal-grid">
      {positions.map(([x, y]) => (
        <button key={`${x}-${y}`}
                onClick={() => onChange({ x, y })}
                aria-label={`Focus ${x} ${y}`}
                className={`aspect-square border ${isActive(x, y) ? "bg-terracotta border-terracotta" : "border-line hover:border-ink"} rounded-sm`} />
      ))}
    </div>
  );
}

// Live focal-point preview. Reacts instantly to grid + advanced changes
// because it renders directly from the same focal state on every keystroke.
function FocalPreview({ label, url, aspect, width, position }) {
  return (
    <div className="text-center" data-testid={`focal-preview-${label.toLowerCase()}`}>
      <div className="text-[9px] label-mono text-ink/40 mb-1 tracking-widest">{label}</div>
      <div className="overflow-hidden bg-line/10 border border-line/50"
           style={{ width: `${width}px`, aspectRatio: aspect }}>
        <img src={url} alt=""
             className="w-full h-full object-cover"
             style={{ objectPosition: position }} />
      </div>
      <div className="text-[9px] text-ink/40 mt-0.5 font-mono">{position}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function TextField({ label, value, onChange, multiline, rows = 3, compact }) {
  return (
    <div>
      {compact
        ? <div className="text-[10px] text-ink/50 uppercase mb-1">{label}</div>
        : <div className="label-mono text-ink/60 text-[10px] mb-2">{label}</div>}
      {multiline
        ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows}
                    className="w-full border border-line px-3 py-2 text-sm" />
        : <input value={value} onChange={e => onChange(e.target.value)}
                 className="w-full border border-line px-3 py-2 text-sm" />}
    </div>
  );
}

function StatsEditor({ draft, patch }) {
  const stats = draft.stats || [];
  return (
    <div className="pt-2 border-t border-line/50">
      <div className="label-mono text-ink/60 text-[10px] mb-2">Statistics</div>
      {stats.map((st, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
          <input value={st.value} onChange={e => { const s = [...stats]; s[i] = { ...s[i], value: e.target.value }; patch("stats", s); }}
                 placeholder="200+" className="col-span-3 border border-line px-2 py-1.5 text-sm" />
          {["da", "en", "es"].map(l => (
            <input key={l} value={st.label?.[l] || ""}
                   onChange={e => { const s = [...stats]; s[i] = { ...s[i], label: { ...(s[i].label || {}), [l]: e.target.value } }; patch("stats", s); }}
                   placeholder={l.toUpperCase()} className="col-span-3 border border-line px-2 py-1.5 text-sm" />
          ))}
          <button onClick={() => { const s = [...stats]; s.splice(i, 1); patch("stats", s); }} className="text-ink/40 hover:text-red-600 text-lg">×</button>
        </div>
      ))}
      <button onClick={() => patch("stats", [...stats, { value: "", label: { da: "", en: "", es: "" } }])}
              className="text-xs uppercase tracking-widest border border-line px-3 py-1.5 hover:border-ink">+ Add stat</button>
    </div>
  );
}

// Lightweight preview — shows headline, body and every image with its
// focal point applied, so admins understand the crop before publishing.
function SectionPreview({ draft, lang }) {
  const t = (obj) => (obj && (obj[lang] || obj.en || obj.da)) || "";
  return (
    <div data-testid="section-preview">
      <div className="text-[10px] label-mono text-ink/40 mb-2">Preview · rendered content only. Publish to see final layout.</div>
      <div className="bg-cream border border-line rounded-lg overflow-hidden">
        <div className="p-6 space-y-3">
          {draft.eyebrow && t(draft.eyebrow) && <div className="label-mono text-terracotta text-[10px]">{t(draft.eyebrow)}</div>}
          {draft.headline && <h4 className="font-editorial text-2xl md:text-3xl tracking-tighter whitespace-pre-line">{t(draft.headline)}</h4>}
          {draft.body && t(draft.body) && <p className="text-sm text-ink/80 whitespace-pre-line">{t(draft.body)}</p>}
          {draft.cta_label && t(draft.cta_label) && (
            <div className="pt-1"><span className="inline-block bg-terracotta text-white px-4 py-2 text-xs uppercase tracking-widest">{t(draft.cta_label)}</span></div>
          )}
        </div>
        {draft.images?.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1 p-1 bg-white">
            {(draft.images || []).filter(i => i?.url).map((im, i) => (
              <div key={i} className="aspect-[4/3] overflow-hidden bg-line/10">
                <img src={resolveImageUrl(im.url)}
                     alt={t(im.alt || {})}
                     className="w-full h-full object-cover"
                     style={{ objectPosition: `${im.focal?.x || 50}% ${im.focal?.y || 50}%` }} />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
